<template>
  <b-col sm="12" :lg="size" :offset="offset">
    <DateModal
      :modalID="chartType"
      :fetchData="fetchData"
      :dateFrom="dateFrom"
      :dateTo="dateTo"
      @date-picker-modal-emit="onDatePickerModalEmit"
    ></DateModal>
    <b-card class="backClass" style="min-height: 400px">
      <div
        slot="header"
        style="display: flex; justify-content: space-between; align-items: center"
      >
        <HeaderTitle
          :title="title"
          :dateFrom="from ? from : dateFrom"
          :dateTo="to ? to : dateTo"
          :chartType="chartType"
        ></HeaderTitle>
        <HeaderControl
          :showFullscreenIcon="true"
          :showDateIcon="true"
          :showDownloadPdfIcon="true"
          :chartType="chartType"
          @emit-fullscreen="toggleFullScreen"
          @emit-date="modalDateEvent(chartType)"
          @emit-last-month="setLastMonth"
          @emit-pdf-download="downloadPDF"
          @emit-excel-download="downloadRecycledExcel"
        ></HeaderControl>
      </div>
      <div class="chart-wrapper">
        <div v-if="loadingData" class="centered_container">
          <LoadingCard></LoadingCard>
        </div>
        <div v-else>
          <div v-if="chartType === 'totalTested' && totalTestedLevel !== 'year'" style="display: flex; justify-content: flex-end;">
            <b-button
              class="btn-outline"
              style="margin-bottom: 0"
              variant="outline-primary"
              @click="drillUpTotalTested"
            >&#8592; Back
            </b-button>
          </div>
          <highcharts
            ref="spaceChartRef"
            :options="chartOptions"
            class="chart"
            :constructor-type="'chart'"
          />
        </div>
      </div>
      <div slot="footer" v-if="chartType !== 'spaceUtilization'">
        <b-button
          v-if="chartType !== 'totalTested'"
          :disabled="!showAll"
          class="btn-outline"
          variant="outline-primary"
          left
          @click="(showAll = false), fetchData()"
        >Top 10
        </b-button>
        <b-button
          v-if="chartType !== 'totalTested'"
          :disabled="showAll"
          class="btn-outline float-right"
          variant="outline-primary"
          right
          @click="(showAll = true), fetchData()"
        >All Data
        </b-button>
        <b-button
          v-if="chartType === 'totalTested'"
          class="btn-outline float-right"
          style="margin-right: 10px"
          variant="outline-primary"
          right
          @click="$router.push({ path: '/base/tables', query: { type: 'totalTested', date_from: dateFrom, date_to: dateTo } })"
        >Table View All Data
        </b-button>
      </div>
    </b-card>
  </b-col>
</template>

<script>
import Highcharts from "highcharts";
import DrilldownModule from "highcharts/modules/drilldown";
import { Chart } from "highcharts-vue";
import HistogramModule from 'highcharts/modules/histogram-bellcurve';
import exporting from "highcharts/modules/exporting";
import HeaderControl from "../shared/HeaderControl.vue";
import HeaderTitle from "../shared/HeaderTitle.vue";
import LoadingCard from "../shared/LoadingCard.vue";
import Datepicker from "vuejs-datepicker";
import DateModal from "../shared/DateModal.vue";
import {chartFunctionsMixin} from "./addons/chartFunctionsMixin.js";
import {chartOptionsMixin} from "./addons/chartOptionsMixin.js";
import { modelStore } from "@/stores/modelStore";

DrilldownModule(Highcharts);
exporting(Highcharts);
HistogramModule(Highcharts);

export default {
  mixins: [chartFunctionsMixin, chartOptionsMixin],
  components: {
    highcharts: Chart,
    Datepicker,
    DateModal,
    HeaderTitle,
    HeaderControl,
    LoadingCard,
  },
  props: [
    "size",
    "offset",
    "title",
    "chartType",
    "firstLabel",
    "categoryLabels",
    "secondLabel",
    "dataArray",
  ],
  data() {
    return {
      dateFrom: "",
      dateTo: "",
      loadingData: true,
      maxValue: 100,
      drilledWarehouse: null,
      drilldownSeriesMap: {},
      formattedData: {},
      totalTestedData: {},
      totalTestedLevel: 'year',
      totalTestedYear: null,
      totalTestedMonth: null,
      chartOptions: {
        chart: {
          type: "column",
          backgroundColor: "transparent",
          marginTop: 70,
          events: {}
        },
        title: {
          text: "",
        },
        subtitle: {
          text: "",
          style: { color: "white" }
        },
        xAxis: {
          type: "category",
          labels: {
            style: {
              color: "white",
            },
          },
        },
        yAxis: {
          min: 0,
          title: {
            text: "Total Tested",
            style: { color: "white" }
          },
          labels: {
            style: { color: "white" }
          },
          gridLineColor: "#4a5568",
          stackLabels: {
            enabled: true,
            style: {
              fontWeight: 'bold',
              color: 'white',
              textOutline: 'none'
            },
            formatter: function() {
              return this.total;
            }
          }
        },
        legend: {
          enabled: false,
          itemStyle: { color: "white" }
        },
        tooltip: {
          shared: true,
          useHTML: true,
          formatter: function () {
            const points = this.points || [this.point];
            const isDrilldown = points.some(p => p.series.name === 'Used' || p.series.name === 'Available');

            if (isDrilldown) {
              const header = points[0].point.name || points[0].key || this.x;
              let s = '<span style="font-size:12px; font-weight: bold; color: white">' + header + '</span><br/>';
              points.forEach(p => {
                const percentage = p.percentage ? p.percentage.toFixed(1) + '%' : '';
                s += '<span style="color:' + p.color + '">\u25CF</span> ' + p.series.name + ': <b>' + p.y + '</b> (' + percentage + ')<br/>';
              });
              return s;
            }
            return '<b>' + (points[0].key || points[0].point.name) + '</b>';
          }
        },
        plotOptions: {
          series: {
            borderRadius: 0,
            cursor: "pointer",
            colorByPoint: false,
            dataLabels: { enabled: false },
            point: {
              events: {
                click: function () {
                  const vm = this.series.chart.renderTo.__vue__;
                  if (vm && vm.drilledWarehouse) {
                    return false; // disable clicks after drilldown
                  }
                }
              }
            }
          },
          column: {
            stacking: 'normal'
          }
        },
        drilldown: {
          activeAxisLabelStyle: { textDecoration: "none", color: "white" },
          activeDataLabelStyle: { color: "white", textDecoration: "none" },
          allowPointDrilldown: false,
          drillUpButton: {
            relativeTo: 'spacingBox',
            position: { align: 'right', x: -10, y: 5 }
          },
          series: []
        },
        series: [],
        credits: { enabled: false }
      },
    };
  },
  computed: {
    selectedModel() { return modelStore.selectedModel; },
    isSpaced() { return modelStore.isSpaced; },
  },
  watch: {
    selectedModel() { if (this.chartType === 'totalTested') this.fetchData(); },
    isSpaced() { if (this.chartType === 'totalTested') this.fetchData(); },
  },
  methods: {
    fetchData() {
      this.loadingData = true;
      this.drilledWarehouse = null;

      if (this.chartType === "spaceUtilization") {
        const link = `${process.env.VUE_APP_TRACKER_BASE}/api/comcast_arc/charts/space_utilization.php`;
        const params = {
          date_from: this.from,
          date_to: this.to,
        };

        this.$http.get(link, { params }).then(
          (res) => {
            this.processSpaceData(res.data || {});
            this.loadingData = false;
          },
          (err) => {
            console.error(err);
            this.loadingData = false;
          }
        );
      } else if (this.chartType === "totalTested") {
        const link = `${process.env.VUE_APP_TRACKER_BASE}/api/comcast_arc/charts/total_tested.php`;
        const productType = modelStore.selectedModel === 'All' ? '' : modelStore.selectedModel;
        const params = {
          date_from: this.from,
          date_to: this.to,
          productType: productType,
          ...(modelStore.isSpaced ? { spaced: true } : {})
        };

        this.$http.get(link, { params }).then(
          (res) => {
            this.processTotalTestedData(res.data || {});
            this.loadingData = false;
          },
          (err) => {
            console.error(err);
            this.loadingData = false;
          }
        );
      } else {
        const data = this.dataArray || [];
        const secondDataArray = data.map((value) => this.maxValue - value);

        this.chartOptions.xAxis.categories = this.categoryLabels;
        this.chartOptions.series = [
          {
            name: this.firstLabel,
            data: data,
            color: "#db5151",
          },
          {
            name: this.secondLabel,
            data: secondDataArray,
            color: "#34db88",
          },
        ];
        this.loadingData = false;
      }
    },
    getDatesInRange(startDate, endDate) {
      if (!startDate || !endDate) return [];
      const dates = [];
      let current = new Date(startDate + "T00:00:00Z");
      const end = new Date(endDate + "T00:00:00Z");
      while (current <= end) {
        dates.push(current.toISOString().split("T")[0]);
        current.setUTCDate(current.getUTCDate() + 1);
      }
      return dates;
    },
    getAggregationKey(date, type) {
      if (type === "daily") return date;
      const d = new Date(date + "T00:00:00Z");
      if (type === "weekly") {
        const day = d.getUTCDay();
        const diff = d.getUTCDate() - day + (day === 0 ? -6 : 1); // Start of week (Monday)
        const startOfWeek = new Date(d.setUTCDate(diff));
        return startOfWeek.toISOString().split("T")[0];
      }
      if (type === "monthly") {
        return date.substring(0, 7) + "-01";
      }
      return date;
    },
    formatPeriodLabel(date, mode) {
      if (!date) return date;
      const d = new Date(date + "T00:00:00Z");
      if (mode === "monthly") {
        return d.toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
      }
      if (mode === "weekly") {
        return "Wk " + d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
      }
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
    },
    processTotalTestedData(rawData) {
      if (!rawData) rawData = {};
      const baseDataMap = {};
      const rawDataArray = Array.isArray(rawData) ? rawData : Object.values(rawData);

      rawDataArray.forEach(item => {
        if (!item) return;
        const dateStr = item.date || "";
        if (!dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return;
        let p_sum = 0, f_sum = 0;
        Object.keys(item).forEach(k => {
          if (!isNaN(k)) {
            p_sum += Number(item[k].passed || 0);
            f_sum += Number(item[k].failure || 0);
          }
        });
        baseDataMap[dateStr] = { passed: p_sum, failure: f_sum };
      });

      this.totalTestedData = baseDataMap;
      this.totalTestedLevel = 'year';
      this.totalTestedYear = null;
      this.totalTestedMonth = null;

      this.chartOptions.subtitle.text = "";
      this.chartOptions.exporting = { enabled: false };
      this.chartOptions.chart.type = "column";
      this.chartOptions.yAxis.reversedStacks = false;
      this.chartOptions.yAxis.stackLabels = { enabled: false };
      this.chartOptions.yAxis.max = 100;
      this.chartOptions.yAxis.title = { text: "", style: { color: "white" } };
      this.chartOptions.yAxis.labels = { enabled: false };
      this.chartOptions.plotOptions.series.dataLabels = { enabled: false };
      this.chartOptions.plotOptions.column.dataLabels = {
        enabled: true,
        color: "white",
        style: { textOutline: 'none', fontWeight: 'bold', fontSize: '10px' },
        formatter: function() {
          return this.y > 0 ? `${this.y.toFixed(1)}%` : null;
        }
      };
      this.chartOptions.tooltip = {
        shared: true,
        formatter: function() {
          const date = this.points[0].point.name;
          let s = `<b>${date}</b><br/>`;
          this.points.forEach(p => {
            s += `${p.series.name}: <b>${p.point.count}</b><br/>`;
          });
          return s;
        }
      };
      this.chartOptions.legend.enabled = true;
      this.chartOptions.legend.itemStyle = { color: "white" };

      // Set click handler once — reads totalTestedLevel/Year dynamically so it never needs reassigning
      this.chartOptions.plotOptions.series.point.events.click = (e) => {
        const key = e.point.options.drillKey;
        if (this.totalTestedLevel === 'year') this.renderTotalTestedChart('month', key);
        else if (this.totalTestedLevel === 'month') this.renderTotalTestedChart('day', this.totalTestedYear, key);
      };

      this.renderTotalTestedChart('year');
    },
    renderTotalTestedChart(level, year, month) {
      this.totalTestedLevel = level;
      this.totalTestedYear = year || null;
      this.totalTestedMonth = month || null;

      const baseDataMap = this.totalTestedData;
      const aggregatedMap = {};

      Object.keys(baseDataMap).sort().forEach(date => {
        if (level === 'month' && date.substring(0, 4) !== year) return;
        if (level === 'day' && date.substring(0, 7) !== month) return;
        const key = level === 'year' ? date.substring(0, 4)
          : level === 'month' ? date.substring(0, 7)
          : date;
        if (!aggregatedMap[key]) aggregatedMap[key] = { passed: 0, failure: 0 };
        aggregatedMap[key].passed += baseDataMap[date].passed;
        aggregatedMap[key].failure += baseDataMap[date].failure;
      });

      const keys = Object.keys(aggregatedMap).sort();
      const passData = [];
      const failData = [];

      keys.forEach(key => {
        const item = aggregatedMap[key];
        const total = item.passed + item.failure;
        const passPercent = total > 0 ? parseFloat((item.passed / total * 100).toFixed(2)) : 0;
        const failPercent = total > 0 ? parseFloat((item.failure / total * 100).toFixed(2)) : 0;
        const label = level === 'month'
          ? new Date(key + '-01T00:00:00Z').toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })
          : key;

        passData.push({ name: label, y: passPercent, count: item.passed, totalCount: total, drillKey: key });
        failData.push({ name: label, y: failPercent, count: item.failure, totalCount: total, drillKey: key });
      });

      this.$nextTick(() => {
        const chart = this.$refs.spaceChartRef && this.$refs.spaceChartRef.chart;
        if (chart && chart.series.length === 2) {
          chart.xAxis[0].setCategories(passData.map(p => p.name), false);
          chart.series[0].setData(failData, false, { duration: 600 });
          chart.series[1].setData(passData, true, { duration: 600 });
        } else {
          this.chartOptions.series = [
            { name: "Fail", data: failData, color: "#db5151" },
            { name: "Pass", data: passData, color: "#34db88" }
          ];
        }
      });
    },
    drillUpTotalTested() {
      if (this.totalTestedLevel === 'day') {
        this.renderTotalTestedChart('month', this.totalTestedYear);
      } else if (this.totalTestedLevel === 'month') {
        this.renderTotalTestedChart('year');
      }
    },
    processSpaceData(rawData) {
      const formatted = {};
      const topLevelData = [];

      Object.keys(rawData).forEach(id => {
        const warehouse = rawData[id];
        const sourceData = warehouse.data || warehouse;
        const dates = Object.keys(sourceData).filter(key => /^\d{4}-\d{2}-\d{2}$/.test(key)).sort();

        const used = dates.map(d => parseFloat(sourceData[d].used || 0));
        const available = dates.map(d => parseFloat(sourceData[d].available || 0));

        formatted[id] = { name: warehouse.name, dates, used, available };

        topLevelData.push({
          name: warehouse.name,
          y: 100,
          color: "#7cb5ec",
          drilldown: id
        });
      });

      this.formattedData = formatted;
      this.chartOptions.series = [{ name: "Warehouses", colorByPoint: true, data: topLevelData }];
      this.chartOptions.subtitle.text = "Click on a warehouse to view space utilization by date";

      this.chartOptions.drilldown.series = Object.keys(formatted).map(id => ({
        id,
        name: formatted[id].name,
        data: []
      }));

      // Drilldown event
      this.chartOptions.chart.events = {
        drilldown: (e) => {
          if (!e.point) return;
          const chart = e.target;
          const warehouseID = e.point.drilldown;
          const warehouse = this.formattedData[warehouseID];
          if (!warehouse) return;

          this.drilledWarehouse = warehouse;
          chart.setTitle(null, { text: "", style: { color: "white" } });
          chart.legend.update({ enabled: true });
          chart.update({ plotOptions: { series: { cursor: 'default' } } });

          const { dates, used, available } = warehouse;

          chart.addSeriesAsDrilldown(e.point, {
            name: "Available",
            type: "column",
            color: "#34db88",
            borderWidth: 0,
            stacking: "normal",
            showInLegend: true,
            dataLabels: { enabled: true, format: "{point.y:.1f}%", color: "white", style: { textOutline: false, fontWeight: 'bold', fontSize: '11px' } },
            data: dates.map((date, i) => ({ name: date, y: available[i] }))
          });

          chart.addSeries({
            name: "Used",
            type: "column",
            color: "#db5151",
            borderWidth: 0,
            stacking: "normal",
            showInLegend: true,
            _isDrilldownSeries: true,
            dataLabels: { enabled: true, format: "{point.y:.1f}%", color: "white", style: { textOutline: false, fontWeight: 'bold', fontSize: '11px' } },
            data: dates.map((date, i) => ({ name: date, y: used[i] }))
          }, true);
        },
        drillup: (e) => {
          const chart = e.target;
          chart.series.filter(s => s.options && s.options._isDrilldownSeries).forEach(s => s.remove(false));
          chart.redraw();

          this.drilledWarehouse = null;
          chart.setTitle(null, { text: "", style: { color: "white" } });
          chart.legend.update({ enabled: false });
          chart.update({ plotOptions: { series: { cursor: 'pointer' } } });
        }
      };

      this.chartOptions.yAxis.max = 100;
      this.chartOptions.yAxis.labels.enabled = false;
      this.chartOptions.yAxis.title.text = "";
    }
  },
  mounted() {
    this.fetchData();
  },
};
</script>

<style>
.highcharts-background {
  fill: #3a4149 !important;
}

.highcharts-exporting-group {
  display: none !important;
}

.vdp-datepicker__calendar {
  position: absolute !important;
  z-index: 100;
  background: #3a4149 !important;
  width: 300px;
  border: 1px solid #ccc;
}

.form-control:disabled,
.form-control[readonly] {
  background-color: #515b65 !important;
  opacity: 1 !important;
}

.centered_container {
  width: 100%;
  display: flex;
  justify-content: center;
}

.chart {
  width: 100%;
}

.custom-table {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #4a5568;
}

.custom-table thead th {
  background-color: #1a202c;
  color: #edf2f7;
  border-bottom: 2px solid #4a5568;
}

.badge-danger {
  background-color: #db5151;
}

.badge-success {
  background-color: #34db88;
}
</style>
